import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import { GlowButton } from '@/components/brand/GlowButton';
import { SectionReveal } from '@/components/brand/SectionReveal';
import { FormField } from '@/components/forms/FormField';
import { createLead } from '@/lib/api';
import { toast } from 'sonner';
import { SERVICE_CATEGORIES } from '@/lib/utils';

const INITIAL_FORM = { name: '', email: '', topic: '', message: '' };

// --- Sub-components ---------------------------------------------------------

const ContactInfo = () => (
    <SectionReveal className="lg:col-span-5">
        <div className="meta-eyebrow">Contact</div>
        <h2 className="mt-5 font-display font-normal text-[34px] sm:text-[40px] lg:text-[46px] leading-[1.1] tracking-[-0.005em] text-la-text text-balance">
            Legal Alliance Consultancy
        </h2>
        <p className="mt-6 text-[14.5px] text-la-text2 leading-relaxed max-w-md">
            Send a short message and we will reply with the next sensible step.
        </p>
        <div className="mt-9 space-y-5 text-[14px]">
            <div className="flex items-start gap-3 text-la-text2">
                <MapPin className="h-4 w-4 text-la-gold mt-0.5 shrink-0" />
                <span className="leading-relaxed">
                    Legal Alliance Consultancy Ltd<br />
                    167–169 Great Portland Street<br />
                    5th Floor<br />
                    London W1W 5PF<br />
                    United Kingdom
                </span>
            </div>
            <a href="mailto:info@legal-ac.co.uk" className="flex items-start gap-3 text-la-text2 hover:text-la-text underline-grow w-fit">
                <Mail className="h-4 w-4 text-la-gold mt-0.5 shrink-0" />
                <span>info@legal-ac.co.uk</span>
            </a>
        </div>
        <p className="mt-9 text-[11.5px] tracking-[0.12em] uppercase text-la-text3">All enquiries handled confidentially.</p>
    </SectionReveal>
);

const TopicSelect = ({ value, onChange }) => (
    <div>
        <label className="meta-eyebrow text-la-text3">Service / Topic</label>
        <select
            value={value} onChange={onChange}
            data-testid="contact-topic"
            className="mt-3 w-full rounded-la-sm bg-white/[0.02] border border-white/10 px-4 py-3 text-[14px] text-la-text focus:outline-none focus:border-la-gold/40"
        >
            <option value="">Select a topic (optional)</option>
            {SERVICE_CATEGORIES.map((s) => (
                <option key={s.slug} value={s.topic}>{s.topic}</option>
            ))}
            <option value="General Enquiry">General Enquiry</option>
        </select>
    </div>
);

const MessageField = ({ value, onChange }) => (
    <div>
        <label className="meta-eyebrow text-la-text3">Message</label>
        <textarea
            value={value} onChange={onChange}
            required rows={5}
            data-testid="contact-message"
            className="mt-3 w-full rounded-la-sm bg-white/[0.02] border border-white/10 px-4 py-3 text-[14px] text-la-text placeholder:text-la-text3 focus:outline-none focus:border-la-gold/40"
            placeholder="A short description is enough."
        />
    </div>
);

const ContactForm = ({ form, onChange, onSubmit, submitting }) => (
    <SectionReveal delay={0.05} className="lg:col-span-7">
        <div className="panel rounded-la p-7 sm:p-10">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FormField label="Name" value={form.name} onChange={onChange('name')} required testId="contact-name" />
                    <FormField label="Email" type="email" value={form.email} onChange={onChange('email')} required testId="contact-email" />
                </div>
                <TopicSelect value={form.topic} onChange={onChange('topic')} />
                <MessageField value={form.message} onChange={onChange('message')} />
                <div className="flex items-center justify-between pt-2">
                    <span className="text-[11px] text-la-text3">All enquiries handled confidentially.</span>
                    <GlowButton type="submit" variant="gold" disabled={submitting} data-testid="contact-submit-button">
                        {submitting ? 'Sending…' : 'Send Message'}
                    </GlowButton>
                </div>
            </form>
        </div>
    </SectionReveal>
);

// --- Main section -----------------------------------------------------------

export const ContactSection = () => {
    const [searchParams] = useSearchParams();
    const incomingTopic = searchParams.get('topic') || '';
    const [form, setForm] = useState({ ...INITIAL_FORM, topic: incomingTopic });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (incomingTopic) setForm((f) => ({ ...f, topic: incomingTopic }));
    }, [incomingTopic]);

    const onChange = useCallback((k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value })), []);

    const submit = useCallback(async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error('Please share your name, email and message.');
            return;
        }
        try {
            setSubmitting(true);
            await createLead({
                name: form.name,
                email: form.email,
                phone: '',
                subject: form.topic ? `Enquiry: ${form.topic}` : 'Website enquiry',
                message: form.message,
                source_page: typeof window !== 'undefined' ? window.location.pathname : '',
            });
            toast.success('Message received. We will reply within one business day.');
            setForm({ ...INITIAL_FORM });
        } catch (err) {
            toast.error('We could not submit your message. Please email us directly.');
        } finally {
            setSubmitting(false);
        }
    }, [form.name, form.email, form.message, form.topic]);

    return (
        <section id="contact" className="relative py-24 sm:py-28">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
                    <ContactInfo />
                    <ContactForm form={form} onChange={onChange} onSubmit={submit} submitting={submitting} />
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
